import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import { normalizeApiError } from '../utils/normalizeApiError';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(localStorage.getItem('access_token'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refresh_token'));
  const [user, setUser] = useState(null);
  const [orgId, setOrgId] = useState(localStorage.getItem('org_id'));
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  // Fetch user permissions
  const fetchPermissions = useCallback(async (accessToken) => {
    if (!accessToken) return null;
    
    try {
      const response = await fetchWithTimeout(`${API_URL}/api/auth/me/permissions`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      
      if (response.ok) {
        return await response.json();
      }
    } catch (err) {
      console.error('Failed to fetch permissions:', err);
    }
    return null;
  }, []);

  // Fetch onboarding status
  const fetchOnboardingStatus = useCallback(async (accessToken) => {
    if (!accessToken) return null;
    
    try {
      const response = await fetchWithTimeout(`${API_URL}/api/auth/me/onboarding-status`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      
      if (response.ok) {
        return await response.json();
      }
    } catch (err) {
      console.error('Failed to fetch onboarding status:', err);
    }
    return null;
  }, []);

  // Validate and load user
  const validateToken = useCallback(async () => {
    const storedToken = localStorage.getItem('access_token');
    
    if (!storedToken) {
      setLoading(false);
      setIsAuthenticated(false);
      return;
    }

    try {
      const response = await fetchWithTimeout(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${storedToken}` }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setToken(storedToken);
        setIsAuthenticated(true);
        
        // Fetch permissions
        const perms = await fetchPermissions(storedToken);
        setPermissions(perms);
        
        // Fetch onboarding status
        const onboardingStatus = await fetchOnboardingStatus(storedToken);
        if (onboardingStatus) {
          setNeedsOnboarding(onboardingStatus.needs_onboarding);
          
          // Set org_id from onboarding status if available
          if (onboardingStatus.org_id && !localStorage.getItem('org_id')) {
            localStorage.setItem('org_id', onboardingStatus.org_id);
            setOrgId(onboardingStatus.org_id);
          }
        }
        
        // Fallback: Set org_id from first org if not set
        if (!localStorage.getItem('org_id') && perms?.organisations?.length > 0) {
          const firstOrg = perms.organisations[0];
          localStorage.setItem('org_id', firstOrg.org_id);
          setOrgId(firstOrg.org_id);
        }
      } else {
        // Token invalid - try refresh
        const refreshed = await refreshAccessToken();
        if (!refreshed) {
          logout();
        }
      }
    } catch (error) {
      console.error('Token validation failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [fetchPermissions, fetchOnboardingStatus]);

  // Refresh access token
  const refreshAccessToken = useCallback(async () => {
    const storedRefresh = localStorage.getItem('refresh_token');
    if (!storedRefresh) return false;

    try {
      const response = await fetchWithTimeout(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: storedRefresh })
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        setToken(data.access_token);
        setRefreshToken(data.refresh_token);
        return true;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
    }
    return false;
  }, []);

  useEffect(() => {
    validateToken();
  }, [validateToken]);

  // Login function
  const login = async (email, password, twoFactorCode = null) => {
    try {
      const body = { email, password };
      if (twoFactorCode) {
        body.two_factor_code = twoFactorCode;
      }
      
      const response = await fetchWithTimeout(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (response.ok) {
        // Check if 2FA is required
        if (data.requires_2fa) {
          return { success: false, requires_2fa: true, message: data.message };
        }
        
        localStorage.setItem('access_token', data.access_token);
        if (data.refresh_token) {
          localStorage.setItem('refresh_token', data.refresh_token);
          setRefreshToken(data.refresh_token);
        }
        if (data.session_id) {
          localStorage.setItem('session_id', data.session_id);
        }
        setToken(data.access_token);
        setIsAuthenticated(true);
        
        // Fetch user and permissions
        const userResponse = await fetchWithTimeout(`${API_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${data.access_token}` }
        });
        if (userResponse.ok) {
          const userData = await userResponse.json();
          setUser(userData);
        }
        
        const perms = await fetchPermissions(data.access_token);
        setPermissions(perms);
        
        // Fetch onboarding status
        const onboardingStatus = await fetchOnboardingStatus(data.access_token);
        if (onboardingStatus) {
          setNeedsOnboarding(onboardingStatus.needs_onboarding);
          
          if (onboardingStatus.org_id) {
            localStorage.setItem('org_id', onboardingStatus.org_id);
            setOrgId(onboardingStatus.org_id);
          }
        }
        
        // Fallback: Set org_id from permissions
        if (!localStorage.getItem('org_id') && perms?.organisations?.length > 0) {
          const firstOrg = perms.organisations[0];
          localStorage.setItem('org_id', firstOrg.org_id);
          setOrgId(firstOrg.org_id);
        }
        
        return { 
          success: true, 
          needsOnboarding: onboardingStatus?.needs_onboarding || false 
        };
      } else {
        return { success: false, error: normalizeApiError(data) };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  // Signup function
  const signup = async (email, password, name) => {
    try {
      const response = await fetchWithTimeout(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('access_token', data.access_token);
        if (data.refresh_token) {
          localStorage.setItem('refresh_token', data.refresh_token);
          setRefreshToken(data.refresh_token);
        }
        if (data.org_id) {
          localStorage.setItem('org_id', data.org_id);
          setOrgId(data.org_id);
        }
        
        setToken(data.access_token);
        setIsAuthenticated(true);
        setNeedsOnboarding(data.needs_onboarding || false);
        
        // Fetch user
        const userResponse = await fetchWithTimeout(`${API_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${data.access_token}` }
        });
        if (userResponse.ok) {
          const userData = await userResponse.json();
          setUser(userData);
        }
        
        const perms = await fetchPermissions(data.access_token);
        setPermissions(perms);
        
        return { 
          success: true, 
          needsOnboarding: data.needs_onboarding,
          orgId: data.org_id
        };
      } else {
        return { success: false, error: normalizeApiError(data) };
      }
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  // Complete onboarding
  const completeOnboarding = async () => {
    try {
      const response = await fetchWithTimeout(`${API_URL}/api/auth/complete-onboarding`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setNeedsOnboarding(false);
        return { success: true };
      } else {
        const data = await response.json();
        return { success: false, error: normalizeApiError(data) };
      }
    } catch (error) {
      console.error('Complete onboarding error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  // Logout function
  const logout = useCallback(async () => {
    const storedToken = localStorage.getItem('access_token');
    
    if (storedToken) {
      try {
        await fetchWithTimeout(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${storedToken}` }
        });
      } catch (error) {
        console.error('Logout API error:', error);
      }
    }

    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('org_id');
    localStorage.removeItem('session_id');
    setToken(null);
    setRefreshToken(null);
    setUser(null);
    setOrgId(null);
    setPermissions(null);
    setIsAuthenticated(false);
    setNeedsOnboarding(false);
  }, []);

  // Bootstrap Founder account
  const bootstrapFounder = async () => {
    try {
      const response = await fetchWithTimeout(`${API_URL}/api/auth/bootstrap-founder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        localStorage.setItem('org_id', data.org_id);
        setToken(data.access_token);
        setRefreshToken(data.refresh_token);
        setOrgId(data.org_id);
        setIsAuthenticated(true);
        
        // Fetch user
        const userResponse = await fetchWithTimeout(`${API_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${data.access_token}` }
        });
        if (userResponse.ok) {
          setUser(await userResponse.json());
        }
        
        const perms = await fetchPermissions(data.access_token);
        setPermissions(perms);
        
        return { success: true, data };
      } else {
        return { success: false, error: normalizeApiError(data) };
      }
    } catch (error) {
      console.error('Bootstrap error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  // Check if user can write (Founder/Owner/Staff)
  const canWrite = useCallback(() => {
    if (!permissions?.organisations || !orgId) return false;
    const org = permissions.organisations.find(o => o.org_id === orgId);
    return org?.can_write || false;
  }, [permissions, orgId]);

  // Check if user can configure (Founder/Owner only)
  const canConfigure = useCallback(() => {
    if (!permissions?.organisations || !orgId) return false;
    const org = permissions.organisations.find(o => o.org_id === orgId);
    return org?.can_configure || false;
  }, [permissions, orgId]);

  // Check if user is Founder
  const isFounder = useCallback(() => {
    if (!permissions?.organisations || !orgId) return false;
    const org = permissions.organisations.find(o => o.org_id === orgId);
    return org?.is_founder || permissions?.is_founder || false;
  }, [permissions, orgId]);

  // Check if user has finance access (Founder OR Manager with finance permission)
  const hasFinanceAccess = useCallback(() => {
    // Founders always have finance access
    if (user?.role === 'FOUNDER') return true;
    
    // Staff NEVER have finance access
    if (user?.role === 'STAFF') return false;
    
    // Managers with explicit finance access
    if (user?.role === 'MANAGER' && user?.manager_finance_access === true) return true;
    
    // Check via permissions
    if (permissions?.is_founder) return true;
    
    return false;
  }, [user, permissions]);

  // Check if user is Staff (for additional UI restrictions)
  const isStaff = useCallback(() => {
    return user?.role === 'STAFF';
  }, [user]);

  // Get auth headers for API calls
  const getAuthHeaders = useCallback(() => {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }, [token]);

  const value = {
    token,
    refreshToken,
    user,
    orgId,
    permissions,
    loading,
    isAuthenticated,
    needsOnboarding,
    login,
    signup,
    logout,
    completeOnboarding,
    bootstrapFounder,
    canWrite,
    canConfigure,
    isFounder,
    isStaff,
    hasFinanceAccess,
    getAuthHeaders,
    setOrgId: (id) => {
      localStorage.setItem('org_id', id);
      setOrgId(id);
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export default AuthContext;
